import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { useAircraftList } from '@/hooks/useAircraft'
import { useLocations } from '@/hooks/useLocations'
import { useHangars } from '@/hooks/useHangars'
import { useUnits } from '@/hooks/useUnits'
import { useTenant } from '@/contexts'
import {
  POSITION_TYPES,
  ALLOCATION_STATUSES,
  POSITION_TYPE_LABELS,
  ALLOCATION_STATUS_LABELS,
} from '@/types/allocations'
import type { AllocationTableRow, AllocationFormData } from '@/types/allocations'
import { allocationFormSchema } from './allocations-form-schema'

interface AllocationFormProps {
  allocation?: AllocationTableRow
  onSubmit: (data: AllocationFormData) => Promise<void>
  isLoading?: boolean
}

export function AllocationForm({ allocation, onSubmit, isLoading = false }: AllocationFormProps) {
  useTenant() // Ensure tenant context is available
  const { data: aircraftList = [] } = useAircraftList()
  const { data: locations = [] } = useLocations()
  const { data: hangars = [] } = useHangars()
  const { data: units = [], refetch: refetchUnits } = useUnits()

  const form = useForm({
    resolver: zodResolver(allocationFormSchema),
    defaultValues: {
      aircraft_id: allocation?.aircraft_id ?? '',
      location_id: allocation?.location_id ?? '',
      position_type: allocation?.position_type ?? 'hangar',
      position_x: allocation?.position_x ?? 0,
      position_y: allocation?.position_y ?? 0,
      position_z: allocation?.position_z ?? 0,
      rotation: allocation?.rotation ?? 0,
      area_id: allocation?.area_id ?? null,
      unit_id: allocation?.unit_id ?? null,
      zone_id: allocation?.zone_id ?? null,
      custom_label: allocation?.custom_label ?? null,
      show_label: allocation?.show_label ?? false,
      color_override: allocation?.color_override ?? '',
      status: allocation?.status ?? 'active',
      lease_contract_id: allocation?.lease_contract_id ?? null,
      transient_stay_id: allocation?.transient_stay_id ?? null,
    },
  })

  const selectedAreaId = form.watch('area_id')

  const handleAreaChange = (areaId: string) => {
    form.setValue('area_id', areaId)
    form.setValue('unit_id', null)
    if (areaId) {
      refetchUnits()
    }
  }

  const filteredUnits = units.filter((unit) => unit.area_id === selectedAreaId)

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Aircraft Selection */}
        <FormField
          control={form.control}
          name="aircraft_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Aircraft</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select aircraft" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {aircraftList.map((aircraft) => (
                    <SelectItem key={aircraft.id} value={aircraft.id}>
                      {aircraft.tail_number || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Location Selection */}
        <FormField
          control={form.control}
          name="location_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Position Type */}
        <FormField
          control={form.control}
          name="position_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Position Type</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select position type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {POSITION_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {POSITION_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Position Coordinates */}
        <div className="grid grid-cols-4 gap-4">
          <FormField
            control={form.control}
            name="position_x"
            render={({ field }) => (
              <FormItem>
                <FormLabel>X</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="0" {...field} onChange={(e) => field.onChange(parseFloat(e.target.value))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="position_y"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Y</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="0" {...field} onChange={(e) => field.onChange(parseFloat(e.target.value))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="position_z"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Z</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="0" {...field} onChange={(e) => field.onChange(parseFloat(e.target.value))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rotation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rotation</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="0" {...field} onChange={(e) => field.onChange(parseFloat(e.target.value))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Area/Building Selection */}
        <FormField
          control={form.control}
          name="area_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Building/Area (Optional)</FormLabel>
              <Select value={field.value || '__none__'} onValueChange={(value) => handleAreaChange(value === '__none__' ? '' : value)}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select building/area" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {hangars.map((hangar) => (
                    <SelectItem key={hangar.id} value={hangar.id}>
                      {hangar.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Unit Selection (filtered by area) */}
        {selectedAreaId && (
          <FormField
            control={form.control}
            name="unit_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit (Optional)</FormLabel>
                <Select value={field.value || '__none__'} onValueChange={(value) => field.onChange(value === '__none__' ? null : value)}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {filteredUnits.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.unit_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Custom Label */}
        <FormField
          control={form.control}
          name="custom_label"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Custom Label (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Custom label" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Show Label Checkbox */}
        <FormField
          control={form.control}
          name="show_label"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel className="font-normal">Show label in 3D view</FormLabel>
            </FormItem>
          )}
        />

        {/* Color Override */}
        <FormField
          control={form.control}
          name="color_override"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Color Override (Optional)</FormLabel>
              <FormControl>
                <Input type="color" {...field} value={field.value || '#000000'} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Status */}
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ALLOCATION_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {ALLOCATION_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Saving...' : allocation ? 'Update Allocation' : 'Create Allocation'}
        </Button>
      </form>
    </Form>
  )
}
